/**
 * Daily ETA board rules (web / informational only).
 * KEEP IN SYNC with plan comments; native reminders do NOT use this module.
 *
 * Slot ends are the "late" anchors (not calendar.end when it differs):
 *   8AM-10AM → 10:00, 9AM-12PM → 12:00, 12PM-3PM → 15:00, 2PM-5PM → 17:00, 4PM-7PM → 19:00
 *
 * Drive:
 *   remote (remote_work or type===2) → 0 minutes
 *   else miles formula via DriveTime.js
 *
 * Assumed onsite when chaining unfinished prior stops:
 *   revisit (type===3) → 30 minutes
 *   else → 120 minutes
 *
 * First leg after SoD: SoD + drive only (no assume-minutes on the first projected stop).
 * Before SoD: no projected ETA, but drive minutes per leg are still computed.
 *
 * Times are FE wall-clock as stored in the DB - no AZ / Phoenix conversion on display.
 */

import moment from 'moment';
import {driveTimeForLeg} from './DriveTime';

export const SLOT_END_HOURS = {
	'8AM-10AM': 10,
	'9AM-12PM': 12,
	'12PM-3PM': 15,
	'2PM-5PM': 17,
	'4PM-7PM': 19,
};

export const ONSITE_ASSUME_NORMAL_MINUTES = 120;
export const ONSITE_ASSUME_REVISIT_MINUTES = 30;

/**
 * Parse a status/calendar timestamp as wall-clock digits (no timezone conversion).
 * Strips any offset/Z so ISO like 2026-07-30T08:08:00-07:00 still displays as 8:08.
 */
export function parseStatusTime(ts) {
	if (!ts) return null;
	const m = moment.parseZone(ts);
	return m.isValid() ? m : null;
}

/** Display DB wall-clock time as-is (FE local), no TZ conversion. */
export function formatStatusTime(ts) {
	const m = parseStatusTime(ts);
	if (!m) return null;
	return m.format('h:mm A');
}

/** "8AM-10AM" → "8 AM - 10 AM" */
export function formatScheduledSlot(label) {
	if (!label) return null;
	return label.replace(/(\d+)(AM|PM)/g, '$1 $2').replace('-', ' - ');
}

export function isRemoteStop(stop) {
	if (!stop) return false;
	if (stop.remote || stop.remote_work) return true;
	const type = Number(stop.type);
	return type === 2;
}

export function isRevisitStop(stop) {
	if (!stop) return false;
	if (stop.revisit) return true;
	return Number(stop.type) === 3;
}

/** PURPOSE: Revisits are short; everything else uses the 2hr chain assumption. */
export function onsiteAssumeMinutes(stop) {
	return isRevisitStop(stop) ? ONSITE_ASSUME_REVISIT_MINUTES : ONSITE_ASSUME_NORMAL_MINUTES;
}

export function resolveSlotLabel(stop) {
	if (stop?.time_slot && SLOT_END_HOURS[stop.time_slot] != null) {
		return stop.time_slot;
	}
	// Infer from start hour when time_slot missing (same buckets as Model::returnTimeSlot).
	const start = stop?.start || stop?.appointment_start;
	if (!start) return null;
	const hi = Number(parseStatusTime(start)?.format('Hmm'));
	if (hi >= 800 && hi < 900) return '8AM-10AM';
	if (hi >= 900 && hi < 1200) return '9AM-12PM';
	if (hi >= 1200 && hi < 1400) return '12PM-3PM';
	if (hi >= 1400 && hi < 1600) return '2PM-5PM';
	if (hi >= 1600) return '4PM-7PM';
	return null;
}

export function slotEndMoment(stop, dayMoment) {
	const label = resolveSlotLabel(stop);
	const hour = label ? SLOT_END_HOURS[label] : null;
	const day = dayMoment
		? moment.parseZone(dayMoment)
		: parseStatusTime(stop?.start || stop?.appointment_start) || moment();
	if (hour == null) {
		const end = stop?.end || stop?.appointment_end;
		return end ? parseStatusTime(end) : null;
	}
	return day.clone().hour(hour).minute(0).second(0).millisecond(0);
}

/**
 * Colors vs slot end:
 *   green  → more than 15 min early
 *   none   → within 15 min of slot end (on-time band, including slightly early)
 *   yellow → late by 1–29 minutes
 *   red    → late by 30+ minutes
 */
export function etaColorVsSlotEnd(etaMoment, slotEnd) {
	if (!etaMoment || !slotEnd) return null;
	const diffMin = moment(slotEnd).diff(moment(etaMoment), 'minutes');
	if (diffMin > 15) return 'green';
	if (diffMin >= 0) return null;
	if (diffMin > -30) return 'yellow';
	return 'red';
}

export function formatEtaTime(etaMoment) {
	if (!etaMoment) return '-';
	return moment.parseZone(etaMoment).format('h:mm A');
}

/**
 * Build projected ETA timeline for one FE day.
 * @param {object} feRow from GET_DAILY_ETA items[]
 * @param {string|Date} [now] optional clock for tests
 */
export function buildFeDayEtas(feRow, now = null) {
	const clock = now ? parseStatusTime(now) || moment(now) : moment();
	const sod = feRow?.sod ? parseStatusTime(feRow.sod) : null;
	const home = {lat: feRow?.latitude, lng: feRow?.longitude};
	const stops = Array.isArray(feRow?.stops) ? [...feRow.stops] : [];

	stops.sort(
		(a, b) =>
			(parseStatusTime(a.start)?.valueOf() || 0) - (parseStatusTime(b.start)?.valueOf() || 0)
	);

	const results = [];
	let prevLeave = sod ? sod.clone() : null;
	let prevCoords = home;

	for (let i = 0; i < stops.length; i++) {
		const stop = stops[i];
		const coords = {
			lat: stop.latitude ?? stop.lat,
			lng: stop.longitude ?? stop.lng,
		};
		const remote = isRemoteStop(stop);
		const driveMinutes = remote
			? 0
			: driveTimeForLeg(prevCoords, coords).drive_time;

		if (!sod) {
			results.push({
				...enrichStopBase(stop, prevCoords, driveMinutes),
				eta: null,
				etaDisplay: null,
				cellDisplay: stop.customer_name || 'Scheduled',
				color: null,
				actual: false,
				pendingSod: true,
			});
		} else {
			const onsiteAt = stop.onsite_at ? parseStatusTime(stop.onsite_at) : null;
			const offsiteAt = stop.offsite_at ? parseStatusTime(stop.offsite_at) : null;
			const slotEnd = slotEndMoment(stop);

			let eta = null;
			let actual = false;

			if (onsiteAt && onsiteAt.isValid()) {
				eta = onsiteAt;
				actual = true;
			} else {
				eta = prevLeave.clone().add(driveMinutes, 'minutes');
			}

			const color = etaColorVsSlotEnd(eta, slotEnd);
			results.push({
				...enrichStopBase(stop, prevCoords, driveMinutes),
				eta,
				etaDisplay: formatEtaTime(eta),
				cellDisplay: formatEtaTime(eta),
				color,
				actual,
				pendingSod: false,
				slotEnd: slotEnd ? slotEnd.format() : null,
				driveMinutes,
			});

			if (offsiteAt && offsiteAt.isValid()) {
				prevLeave = offsiteAt;
			} else if (onsiteAt && onsiteAt.isValid()) {
				const assumedEnd = onsiteAt.clone().add(onsiteAssumeMinutes(stop), 'minutes');
				prevLeave = assumedEnd.isAfter(clock) ? assumedEnd : clock.clone();
			} else if (eta) {
				prevLeave = eta.clone().add(onsiteAssumeMinutes(stop), 'minutes');
			}
		}

		if (!remote && coords.lat != null && coords.lng != null) {
			prevCoords = coords;
		}
	}

	return {sod: feRow?.sod ?? null, stops: results};
}

function enrichStopBase(stop, fromCoords, driveMinutes) {
	return {
		ticket: stop.ticket,
		calendar_pkey: stop.calendar_pkey,
		customer_name: stop.customer_name,
		fresh_ticket: stop.fresh_ticket,
		start: stop.start,
		time_slot: stop.time_slot,
		remote: isRemoteStop(stop),
		revisit: isRevisitStop(stop),
		progress: stop.progress,
		onsite_at: stop.onsite_at,
		offsite_at: stop.offsite_at,
		time_on_site: stop.time_on_site,
		drive_to: isRemoteStop(stop) ? 0 : driveMinutes,
		from_coords: fromCoords,
	};
}

/** Numeric metro-walk region order (missing → last). */
export function regionRankingValue(regionOrFe) {
	const rank =
		regionOrFe?.metro_walk_ranking ??
		regionOrFe?.region_metro_walk_ranking ??
		regionOrFe?.regions?.metro_walk_ranking;
	if (rank === null || rank === undefined || rank === '') return 999;
	return Number(rank);
}
/** Sort FE rows: metro-walk region order then name alpha. */
export function sortFesByRegionThenName(items) {
	const list = Array.isArray(items) ? [...items] : [];
	list.sort((a, b) => {
		const ra = regionRankingValue(a);
		const rb = regionRankingValue(b);
		if (ra !== rb) return ra - rb;
		const na = (a.name || `${a.name_first || ''} ${a.name_last || ''}`).trim();
		const nb = (b.name || `${b.name_first || ''} ${b.name_last || ''}`).trim();
		return na.localeCompare(nb);
	});
	return list;
}
